import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StoryRailItem from './StoryRailItem';
import YourStoryTile from './YourStoryTile';
import AddStoryBottomSheet from '../dialogs/AddStoryBottomSheet';
import { useAuth } from '../../contexts/AuthContext';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import {
  getCrewStoriesFeed,
  getMyStories,
  getViewedVersions,
  healStoriesBackdrops,
  type Story,
} from '../../services/storiesService';
import { edgeToEdgeSx, scrollFadeMaskSx } from '../../utils/scrollStyles';

interface AuthorGroup {
  authorUid: string;
  authorName: string;
  stories: Story[];
  hasUnseen: boolean;
  newestCreatedAt: string;
}

interface StoriesRailProps {
  /** Increment to trigger a manual refresh (e.g. from pull-to-refresh). */
  refreshKey?: number;
}

const SHIMMER_COUNT = 4;
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

// Module-level cache — survives navigation unmounts, scoped to a single user
let _cachedGroups: AuthorGroup[] | null = null;
let _cacheTimestamp = 0;
let _cacheUserUid: string | null = null;
let _cacheFollowedCount = 0; // tracks how many followed users were in scope when cache was built

function getCachedGroupsForUser(uid: string): AuthorGroup[] | null {
  if (_cachedGroups === null || _cacheUserUid !== uid) return null;
  return _cachedGroups;
}

export function invalidateStoriesCache(): void {
  _cachedGroups = null;
  _cacheTimestamp = 0;
}

function sortGroupsForUser(groups: AuthorGroup[], uid: string): AuthorGroup[] {
  return [...groups].sort((a, b) => {
    if (a.authorUid === uid) return -1;
    if (b.authorUid === uid) return 1;
    if (a.hasUnseen !== b.hasUnseen) return a.hasUnseen ? -1 : 1;
    return b.newestCreatedAt.localeCompare(a.newestCreatedAt);
  });
}

const StoriesRail: React.FC<StoriesRailProps> = ({ refreshKey = 0 }) => {
  const [addStoryOpen, setAddStoryOpen] = useState(false);
  const { user } = useAuth();
  const { followStats } = useUserFollow();
  const followedUsers = followStats.followedUsers;
  const { getUserDisplayName, getUserAvatar, preloadProfiles } = useUserProfileCache();
  const navigate = useNavigate();

  const cachedForUser = user ? getCachedGroupsForUser(user.uid) : null;
  const [groups, setGroups] = useState<AuthorGroup[]>(cachedForUser ?? []);
  const [loading, setLoading] = useState(cachedForUser === null);
  const mountedRef = useRef(true);

  const loadStories = useCallback(async (force = false) => {
    if (!user) return;
    // Skip fetch if cache is fresh, belongs to this user, and follow list hasn't grown since last cache
    const followCount = followedUsers.filter((uid) => uid !== user.uid).length;
    if (
      !force &&
      _cachedGroups !== null &&
      _cacheUserUid === user.uid &&
      followCount <= _cacheFollowedCount &&
      Date.now() - _cacheTimestamp < CACHE_TTL_MS
    ) return;
    try {
      const crewUids = followedUsers.filter((uid) => uid !== user.uid);

      const [crewStories, myStories] = await Promise.all([
        crewUids.length > 0 ? getCrewStoriesFeed(crewUids) : Promise.resolve([] as Story[]),
        getMyStories(user.uid),
      ]);

      const allStories = [...myStories, ...crewStories];
      if (allStories.length === 0) {
        _cachedGroups = [];
        _cacheTimestamp = Date.now();
        _cacheUserUid = user.uid;
        _cacheFollowedCount = followCount;
        if (mountedRef.current) {
          setGroups([]);
          setLoading(false);
        }
        return;
      }

      // Heal own stories missing backdropPath — once per session, non-blocking
      healStoriesBackdrops(user.uid).catch(() => {});

      const uniqueAuthorUids = [...new Set(allStories.map((s) => s.authorUid))];
      await preloadProfiles(uniqueAuthorUids).catch(() => {});

      const allStoryIds = allStories.map((s) => s.id);
      const viewedVersions = await getViewedVersions(user.uid, allStoryIds);

      const byAuthor = new Map<string, Story[]>();
      for (const story of allStories) {
        const arr = byAuthor.get(story.authorUid) ?? [];
        arr.push(story);
        byAuthor.set(story.authorUid, arr);
      }

      const authorGroups: AuthorGroup[] = [];
      for (const [authorUid, authorStories] of byAuthor.entries()) {
        authorStories.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        const hasUnseen = authorStories.some((s) => {
          const viewedVersion = viewedVersions.get(s.id);
          return viewedVersion === undefined || viewedVersion < s.contentVersion;
        });

        authorGroups.push({
          authorUid,
          authorName: getUserDisplayName(authorUid, ''),
          stories: authorStories,
          hasUnseen,
          newestCreatedAt: authorStories[0].createdAt,
        });
      }

      const sorted = sortGroupsForUser(authorGroups, user.uid);

      _cachedGroups = sorted;
      _cacheTimestamp = Date.now();
      _cacheUserUid = user.uid;
      _cacheFollowedCount = followCount;
      if (mountedRef.current) {
        setGroups(sorted);
        setLoading(false);
      }
    } catch (err) {
      console.error('StoriesRail: failed to load stories', err);
      if (mountedRef.current) setLoading(false);
    }
  }, [user, followedUsers, getUserDisplayName, preloadProfiles]);

  useEffect(() => {
    mountedRef.current = true;
    loadStories();
    return () => { mountedRef.current = false; };
  }, [loadStories]);

  // Re-fetch when refreshKey changes (pull-to-refresh from dashboard) — always force
  useEffect(() => {
    if (refreshKey > 0) loadStories(true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when window is focused (e.g. after posting a story)
  useEffect(() => {
    const onFocus = () => loadStories();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadStories]);

  if (!user) return null;

  const ownGroup = groups.find((g) => g.authorUid === user.uid);
  const crewGroups = groups.filter((g) => g.authorUid !== user.uid);

  return (
    <>
    <Box
      sx={{
        overflowX: 'auto',
        overflowY: 'visible',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        mb: 2,
        ...edgeToEdgeSx,
        ...scrollFadeMaskSx,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, width: 'max-content', py: 0.5 }}>
        {loading ? (
          Array.from({ length: SHIMMER_COUNT }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                width: 72,
                flexShrink: 0,
              }}
            >
              <Skeleton variant="circular" width={60} height={60} />
              <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '20px' }} />
            </Box>
          ))
        ) : (
          <>
            {/* YourStoryTile is always the leftmost tile */}
            <YourStoryTile
              ownStories={ownGroup?.stories ?? []}
              avatarUrl={getUserAvatar(user.uid)}
              userName={getUserDisplayName(user.uid, user.displayName ?? undefined)}
              onViewStories={
                ownGroup
                  ? () =>
                      navigate(`/stories/${user.uid}`, {
                        state: {
                          stories: ownGroup.stories,
                          authorUids: groups.map((g) => g.authorUid),
                          authorNames: groups.reduce<Record<string, string>>((acc, g) => {
                            acc[g.authorUid] = g.authorName;
                            return acc;
                          }, {}),
                          authorIndex: groups.findIndex((g) => g.authorUid === user.uid),
                        },
                      })
                  : undefined
              }
              onAddStory={() => setAddStoryOpen(true)}
            />

            {/* Crew members' stories */}
            {crewGroups.map((group, idx) => (
              <StoryRailItem
                key={group.authorUid}
                authorUid={group.authorUid}
                authorName={group.authorName}
                avatarUrl={getUserAvatar(group.authorUid)}
                stories={group.stories}
                hasUnseen={group.hasUnseen}
                onClick={() =>
                  navigate(`/stories/${group.authorUid}`, {
                    state: {
                      stories: group.stories,
                      authorUids: crewGroups.map((g) => g.authorUid),
                      authorNames: crewGroups.reduce<Record<string, string>>((acc, g) => {
                        acc[g.authorUid] = g.authorName;
                        return acc;
                      }, {}),
                      authorIndex: idx,
                    },
                  })
                }
              />
            ))}
          </>
        )}
      </Box>
    </Box>

      <AddStoryBottomSheet
        open={addStoryOpen}
        onClose={() => {
          setAddStoryOpen(false);
          loadStories(true);
        }}
      />
    </>
  );
};

export default StoriesRail;
